import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { PageHeader } from '../components/PageHeader';
import { useCreateExpense, useDeleteExpense, useExpenses, useProjects } from '../hooks/useOps';
import type { Expense, ExpenseCategory } from '../api/types';
import { formatDate, formatIDR, humanizeStatus, statusColor } from '../lib/format';

const CATEGORIES: ExpenseCategory[] = [
  'infra',
  'saas',
  'ai_api',
  'contractor',
  'office',
  'tax',
  'other',
];

export function ExpensesPage() {
  const { data, isLoading, error } = useExpenses();
  const { data: projects } = useProjects();
  const createExpense = useCreateExpense();
  const deleteExpense = useDeleteExpense();

  // Per-project cost attribution is the point of housing finance here — the
  // Money tab (P5) sums exactly these tagged rows as the project's direct cost.
  const projectName = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of projects ?? []) map.set(p.id, `${p.code} — ${p.name}`);
    return map;
  }, [projects]);

  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<ExpenseCategory>('saas');
  const [vendorName, setVendorName] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState(0);
  const [ppnInput, setPpnInput] = useState(0);
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().slice(0, 10));
  const [projectId, setProjectId] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringInterval, setRecurringInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [formError, setFormError] = useState<string | null>(null);

  const openCreate = () => {
    setCategory('saas');
    setVendorName('');
    setDescription('');
    setAmount(0);
    setPpnInput(0);
    setExpenseDate(new Date().toISOString().slice(0, 10));
    setProjectId('');
    setIsRecurring(false);
    setFormError(null);
    setOpen(true);
  };

  const submit = async () => {
    try {
      await createExpense.mutateAsync({
        category,
        vendorName: vendorName || undefined,
        description,
        amount,
        ppnInputAmount: ppnInput || undefined,
        expenseDate,
        projectId: projectId || undefined,
        isRecurring,
        recurringInterval: isRecurring ? recurringInterval : undefined,
      });
      setOpen(false);
    } catch (e) {
      setFormError((e as Error).message);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this expense?')) return;
    await deleteExpense.mutateAsync(id);
  };

  const columns: GridColDef<Expense>[] = [
    { field: 'expenseDate', headerName: 'Date', width: 120, valueGetter: (v) => formatDate(v as string) },
    {
      field: 'category',
      headerName: 'Category',
      width: 120,
      valueGetter: (v) => humanizeStatus(v as string),
    },
    { field: 'vendorName', headerName: 'Vendor', minWidth: 150, valueGetter: (v) => v ?? '—' },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
    {
      field: 'projectId',
      headerName: 'Project',
      minWidth: 160,
      valueGetter: (v) => (v ? (projectName.get(v as string) ?? '…') : '—'),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (v) => formatIDR(v as string),
    },
    {
      field: 'isRecurring',
      headerName: 'Recurring',
      width: 110,
      renderCell: (p) =>
        p.value ? (
          <Chip size="small" label={humanizeStatus(p.row.recurringInterval ?? 'yes')} variant="outlined" />
        ) : (
          '—'
        ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: (p) => (
        <Chip size="small" label={humanizeStatus(p.value)} color={statusColor(p.value)} variant="outlined" />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 70,
      sortable: false,
      renderCell: (p) => (
        <IconButton size="small" onClick={() => remove(p.row.id)} aria-label="delete">
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Expenses"
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New expense
          </Button>
        }
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message}
        </Alert>
      )}
      <Paper variant="outlined" sx={{ height: 600 }}>
        <DataGrid
          rows={data ?? []}
          columns={columns}
          getRowId={(r) => r.id}
          loading={isLoading}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        />
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New expense</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <Stack direction="row" gap={2}>
              <TextField
                select
                label="Category"
                value={category}
                onChange={(e) => setCategory(e.target.value as ExpenseCategory)}
                sx={{ flex: 1 }}
              >
                {CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {humanizeStatus(c)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Date"
                type="date"
                value={expenseDate}
                onChange={(e) => setExpenseDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <TextField
              select
              label="Project (optional — tags this cost to a project's P&L)"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
            >
              <MenuItem value="">
                <em>None — overhead</em>
              </MenuItem>
              {(projects ?? []).map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.code} — {p.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField label="Vendor" value={vendorName} onChange={(e) => setVendorName(e.target.value)} />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
            <Stack direction="row" gap={2}>
              <TextField
                label="Amount"
                type="number"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Creditable input PPN"
                type="number"
                value={ppnInput}
                onChange={(e) => setPpnInput(Number(e.target.value))}
                sx={{ flex: 1 }}
              />
            </Stack>
            <Stack direction="row" gap={2} alignItems="center">
              <FormControlLabel
                control={
                  <Switch checked={isRecurring} onChange={(e) => setIsRecurring(e.target.checked)} />
                }
                label="Recurring"
              />
              {isRecurring && (
                <TextField
                  select
                  label="Interval"
                  value={recurringInterval}
                  onChange={(e) => setRecurringInterval(e.target.value as 'monthly' | 'yearly')}
                  sx={{ width: 160 }}
                  size="small"
                >
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                </TextField>
              )}
            </Stack>
            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submit}
            disabled={!description.trim() || amount <= 0 || createExpense.isPending}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
